import { useState } from 'react';
import { useTrunks } from '../../lib/api/hooks-billing.js';
import {
  useTenantTrunks, useLinkTrunk, useUnlinkTrunk, useOwnTrunks, useSetTrunkMode,
} from '../../lib/api/hooks-products.js';
import { useTenant } from '../../lib/api/hooks.js';
import { OwnTrunksPanel } from '../carriers/OwnTrunksPanel.js';
import { TableSkeleton, Empty, ErrorBanner, SectionHead } from '../../components/ui/Primitives.js';
import { confirmAction } from '../../components/ui/Confirm.js';
import { HealthBadge, RegistrationBadge } from '../carriers/TrunkLive.js';
import { Icon } from '../../components/ui/Icon.js';
import { useSession } from '../../lib/auth/session.js';

/**
 * Trunk assignment for one account.
 *
 * A customer routes over SEVERAL carriers as the normal case. Priority is what
 * makes failover deterministic: lower runs first, and the database refuses two
 * enabled trunks at the same priority for a direction, because that would make
 * routing order depend on row order rather than on a decision someone made.
 */
export function AccountTrunksTab({ tenantId }: { tenantId: string }) {
  const tenant = useTenant(tenantId);
  const mode = tenant.data?.trunk_mode ?? 'arcnet';
  const links = useTenantTrunks(tenantId);
  const ours = links.data?.data.filter((l) => !l.owned) ?? [];

  return (
    <>
      {tenant.data && <TrunkModeSwitch tenantId={tenantId} mode={mode} name={tenant.data.name} />}
      {mode === 'byot' ? (
        <>
          <OwnTrunksPanel tenantId={tenantId} />
          {ours.length > 0 && (
            <p className="arc-subtle" style={{ fontSize: 12, marginTop: 10 }}>
              {ours.length} ArcNet trunk link{ours.length === 1 ? '' : 's'} kept for switching back.
            </p>
          )}
        </>
      ) : (
        <ArcnetTrunks tenantId={tenantId} />
      )}
    </>
  );
}

/**
 * Our trunks, or the customer's own: the provider's choice, per account.
 *
 * Nothing is deleted either way. The node carries only the set the mode
 * selects, so the switch takes effect on its next sync (within a minute) and
 * switching back restores the old links as they were. What it does NOT do is
 * wait for the customer: switching to BYOT before their trunk is configured
 * leaves the account with no outside calls, which is why it says so first.
 */
function TrunkModeSwitch({ tenantId, mode, name }: {
  tenantId: string; mode: 'arcnet' | 'byot'; name: string;
}) {
  const can = useSession((s) => s.can);
  const set = useSetTrunkMode(tenantId);
  const own = useOwnTrunks(tenantId);
  const mayChange = can('trunk:write');
  const ownReady = (own.data?.data ?? []).some((t) => t.status === 'active');

  const choose = async (next: 'arcnet' | 'byot') => {
    if (next === mode || set.isPending) return;
    const title = next === 'byot'
      ? `Switch ${name} to its own trunk?`
      : `Switch ${name} back to ArcNet trunks?`;
    const body = next === 'byot'
      ? `Its calls stop using ArcNet trunks within a minute.${ownReady ? '' : ' It has no own trunk yet, so outside calls fail until one is added.'}`
      : 'Its calls use the linked ArcNet trunks within a minute.';
    if (await confirmAction({ title, body, action: 'Switch' })) set.mutate(next);
  };

  // The portal's own choice cards (arc-kind, as Media uses): two ways of
  // carrying calls, each with the one line that says what it means.
  const OPTIONS: Array<{ value: 'arcnet' | 'byot'; icon: 'server' | 'carrier'; label: string; hint: string }> = [
    { value: 'arcnet', icon: 'server', label: 'ArcNet trunk', hint: 'Calls use our carriers' },
    { value: 'byot', icon: 'carrier', label: 'Own trunk', hint: 'Calls use the customer’s carrier account' },
  ];

  return (
    <>
      <div className="arc-kind-grid">
        {OPTIONS.map((o) => (
          <button key={o.value} type="button" className="arc-kind"
            data-on={mode === o.value} aria-pressed={mode === o.value}
            disabled={!mayChange || set.isPending}
            title={mayChange ? undefined : 'Changing this needs the Modify trunks permission'}
            onClick={() => void choose(o.value)}>
            <span className="arc-kind-icon"><Icon name={o.icon} size={18} /></span>
            <span className="arc-kind-body">
              <span className="arc-kind-label">{o.label}</span>
              <span className="arc-kind-hint">{o.hint}</span>
            </span>
          </button>
        ))}
      </div>
    </>
  );
}

/** ArcNet mode: our trunks, linked to this account in priority order. */
function ArcnetTrunks({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const allLinks = useTenantTrunks(tenantId);
  const allTrunks = useTrunks();
  const unlink = useUnlinkTrunk(tenantId);
  const [adding, setAdding] = useState(false);

  // A customer's own trunk is kept while the account is on ours, but it is not
  // one of ours to order or unlink here.
  const ownKept = allLinks.data?.data.filter((l) => l.owned).length ?? 0;
  const links = { ...allLinks, data: allLinks.data ? { data: allLinks.data.data.filter((l) => !l.owned) } : undefined };

  const linkedIds = new Set(links.data?.data.map((l) => l.trunk_id) ?? []);
  const available = (allTrunks.data?.data ?? []).filter(
    (t) => !linkedIds.has(t.id) && t.status !== 'disabled',
  );

  const outbound = links.data?.data.filter((l) => l.direction !== 'inbound') ?? [];

  return (
    <>
      {ownKept > 0 && (
        <p className="arc-subtle" style={{ fontSize: 12, marginTop: -6, marginBottom: 12 }}>
          The customer’s own trunk is kept for switching back.
        </p>
      )}
      <SectionHead level="section" title="Carrier trunks"
        sub="Outbound calls try these in priority order and fall through on failure."
        action={can('trunk:write') && (
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
            onClick={() => setAdding((v) => !v)} disabled={adding || available.length === 0}>
            <Icon name="plus" size={13} /> Link a trunk
          </button>
        )} />

      {links.error && <ErrorBanner error={links.error} />}

      {outbound.length === 1 && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <div>
            <div style={{ fontWeight: 600 }}>Single outbound carrier</div>
            <div style={{ fontSize: 12.5 }}>
              This account has no failover. If this carrier drops, every outbound
              call fails until it returns.
            </div>
          </div>
        </div>
      )}

      {adding && (
        <LinkTrunkForm
          tenantId={tenantId}
          available={available}
          usedPriorities={new Set(links.data?.data.filter((l) => l.is_enabled).map((l) => `${l.direction}:${l.priority}`))}
          onDone={() => setAdding(false)}
        />
      )}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th className="arc-td-right">Priority</th>
                <th>Trunk</th><th>Carrier</th><th>Direction</th>
                <th>Status</th><th>Registration</th><th>Presented CLI</th>
                <th className="arc-td-right">Channel cap</th><th />
              </tr>
            </thead>
            {links.data === undefined ? <TableSkeleton query={links} cols={9} /> : (
              <tbody>
                {links.data?.data.map((l) => (
                  <tr key={l.id} style={l.is_enabled ? undefined : { opacity: .5 }}>
                    <td className="arc-td-right arc-num" style={{ fontWeight: 600 }}>{l.priority}</td>
                    <td>
                      <div className="arc-stack">
                        <span style={{ fontWeight: 550 }}>{l.trunk_name}</span>
                        <span className="arc-subtle arc-mono" style={{ fontSize: 11.5 }}>{l.trunk_host}</span>
                      </div>
                    </td>
                    <td>{l.carrier_name}</td>
                    <td>{l.direction}</td>
                    {/* Measured by the switch (TrunkLive.tsx), not configured. */}
                    <td><HealthBadge live={l.live} /></td>
                    <td><RegistrationBadge live={l.live} authMode={l.auth_mode} /></td>
                    <td className="arc-mono arc-subtle">
                      {l.override_caller_id ?? <span className="arc-subtle">trunk default</span>}
                    </td>
                    <td className="arc-td-right arc-num">{l.max_concurrent_calls ?? '—'}</td>
                    <td className="arc-td-right">
                      {can('trunk:write') && (
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => {
                            void confirmAction({
                              title: `Unlink ${l.trunk_name} from this account?`,
                              body: 'Calls will stop routing over it immediately.',
                              action: 'Unlink', danger: true,
                            }).then((ok) => { if (ok) unlink.mutate(l.id); });
                          }}>
                          Unlink
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {links.data !== undefined && links.data?.data.length === 0 && (
          <Empty
            icon="carrier"
            title="No trunks linked"
            hint="Without a trunk this account can make on-net calls only — nothing reaches the PSTN."
          />
        )}
      </div>
    </>
  );
}

function LinkTrunkForm({ tenantId, available, usedPriorities, onDone }: {
  tenantId: string;
  available: Array<{ id: string; name: string; carrier_name?: string | undefined; direction: string }>;
  usedPriorities: Set<string>;
  onDone: () => void;
}) {
  const link = useLinkTrunk(tenantId);
  const [trunkId, setTrunkId] = useState(available[0]?.id ?? '');
  const [direction, setDirection] = useState('both');
  const [priority, setPriority] = useState('100');
  const [cli, setCli] = useState('');

  const clash = usedPriorities.has(`${direction}:${Number(priority)}`);

  return (
    <form
      className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e) => {
        e.preventDefault();
        link.mutate(
          {
            trunk_id: trunkId, direction, priority: Number(priority),
            is_enabled: true,
            ...(cli ? { override_caller_id: cli } : {}),
          },
          { onSuccess: () => { setCli(''); onDone(); } },
        );
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 110px 1fr auto', gap: 12, alignItems: 'start' }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="lt-trunk">Trunk</label>
          <select id="lt-trunk" className="arc-select" value={trunkId}
            onChange={(e) => setTrunkId(e.target.value)} required>
            {available.map((t) => (
              <option key={t.id} value={t.id}>
                {t.carrier_name ? `${t.carrier_name} — ` : ''}{t.name} ({t.direction})
              </option>
            ))}
          </select>
        </div>

        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="lt-dir">Direction</label>
          <select id="lt-dir" className="arc-select" value={direction}
            onChange={(e) => setDirection(e.target.value)}>
            <option value="both">Both</option>
            <option value="outbound">Outbound</option>
            <option value="inbound">Inbound</option>
          </select>
        </div>

        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="lt-pri">Priority</label>
          <input id="lt-pri" className="arc-input" inputMode="numeric" value={priority}
            onChange={(e) => setPriority(e.target.value)} aria-invalid={clash} />
          {clash
            ? <div className="arc-error">Already used for {direction}</div>
            : <div className="arc-hint">Lower first</div>}
        </div>

        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="lt-cli">Presented CLI</label>
          <input id="lt-cli" className="arc-input" placeholder="+27115550100" value={cli}
            onChange={(e) => setCli(e.target.value)} />
        </div>

        <div style={{ paddingTop: 25 }}>
          <button type="submit" className="arc-btn arc-btn-primary"
            disabled={link.isPending || clash || !trunkId}>
            {link.isPending ? 'Linking…' : 'Link'}
          </button>
          <button type="button" className="arc-btn" style={{ marginLeft: 8 }} onClick={onDone}>Cancel</button>
        </div>
      </div>
    </form>
  );
}
